

export interface IAgency {
  readonly id: number;
  readonly name: string;
  readonly acronym: string;
  readonly areaPlanId: number;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly deletedAt: string | null;
  readonly canManageAnyAgency: number;
}

export interface IWarehouse {
  readonly id: number;
  readonly name: string;
  readonly agency: IAgency;
  readonly hasAccess: number;
}

export interface ICheckoutUser {
  readonly id: number;
  readonly representationName: string;
  readonly registration: string | null;
  readonly login: string;
}

export interface IEquipment {
  readonly active: number;
  readonly code: string;
  readonly equipmentGroup: IEquipmentGroup
  readonly hasAccess: number;
  readonly id: number;
  readonly isAvailable: number;
  readonly name: string;
  readonly serialNumber: string;
  readonly deletedAt: string | null;
  readonly warehouseId?: number;
  readonly equipmentId?: number;
  readonly agencyId?: number;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface IEquipmentGroup {
  readonly createdAt: string;
  readonly deletedAt: string | null;
  readonly id: number;
  readonly name: string;
  readonly updatedAt: string;
}

export interface IEquipmentDetail extends IEquipment {
  readonly equipmentGroup: IEquipmentGroup;
  readonly agency: IAgency;
}

export interface ItemCheckout {
  readonly itemId: number | null
  readonly groupId: number | null
  readonly quantity: number | null
  readonly equipment: IEquipmentDetail | null
}

export interface ICheckoutEquipment {
  readonly createdAt: string;
  readonly deletedAt: string | null;
  readonly disposed: number;
  readonly equipment: IEquipment;
  readonly equipmentId: number;
  readonly id: number;
  readonly lost: number;
  readonly notes: string | null;
  readonly returnedDamaged: number;
  readonly updatedAt: string;
  readonly warehouseCheckoutId: number;
}

export interface IMaterialGroup {
  readonly id: number;
  readonly name: string;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly deletedAt: string | null;
}

export interface IMeasurementUnit {
  readonly id: number;
  readonly name: string;
  readonly symbol: string;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly deletedAt: string | null;
}

export interface IMaterial {
  readonly id: number;
  readonly name: string;
  readonly code: string;
  readonly materialGroup: IMaterialGroup;
  readonly materialGroupId: number;
  readonly measurementUnitId: number;
  readonly totalInventory: number;
  readonly reservedInventory: number;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly deletedAt: string | null;
}

export interface IMaterialDetail extends IMaterial {
  readonly materialGroup: IMaterialGroup;
  readonly measurementUnit: IMeasurementUnit;
}

export interface ItemMaterialCheckout {
  readonly itemId: number | null
  readonly groupId: number | null
  readonly quantity: number | null
  readonly material: IMaterialDetail | null
}

export interface ICheckoutMaterialGroup {
  readonly id: number;
  readonly name: string;
}

export interface ICheckoutMaterialItem {
  readonly id: number;
  readonly name: string;
  readonly code: string;
  readonly materialGroup: ICheckoutMaterialGroup | null;
}

export interface ICheckoutMaterial {
  readonly id: number;
  readonly quantityAtCheckout: number;
  readonly quantityAtCheckin: number | null;
  readonly notes: string | null;
  readonly material: ICheckoutMaterialItem;
}

export interface IWarehouseCheckout {
  readonly id: number;
  readonly warehouseId: number;
  readonly checkedOutByUserId: number;
  readonly checkedInByUserId: number | null;
  readonly receivedByUserId: number;
  readonly checkedOutStationId: number;
  readonly checkedInStationId: number | null;
  readonly status: number;
  readonly notes: string | null;
  readonly checkedOutAt: string;
  readonly checkedInAt: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly deletedAt: string | null;
  readonly checkedInByUser: ICheckoutUser | null;
  readonly warehouse: IWarehouse;
  readonly checkedOutByUser: ICheckoutUser;
  readonly receivedByUser: ICheckoutUser;
  readonly checkoutEquipments: readonly ICheckoutEquipment[];
  readonly checkoutMaterials: readonly ICheckoutMaterial[];
  readonly statusLabel: string;
}

// ─── Raw API types (snake_case) ──────────────────────────────────────────────

interface RawAgency {
  readonly id: number;
  readonly name: string;
  readonly acronym: string;
  readonly area_plan_id: number;
  readonly created_at: string;
  readonly updated_at: string;
  readonly deleted_at: string | null;
  readonly can_manage_any_agency: number;
}

interface RawWarehouse {
  readonly id: number;
  readonly name: string;
  readonly agency: RawAgency;
  readonly hasAccess: number;
}

interface RawCheckoutUser {
  readonly id: number;
  readonly representation_name: string;
  readonly registration: string | null;
  readonly login: string;
}

interface RawEquipment {
  readonly id: number;
  readonly name: string;
  readonly code: string;
  readonly serial_number: string;
  readonly warehouse_id?: number;
  readonly equipment_id?: number;
  readonly agency_id?: number;
  readonly is_available: number;
  readonly active: number;
  readonly created_at: string;
  readonly updated_at: string;
  readonly deleted_at: string | null;
  readonly hasAccess: number;
  readonly equipment_group: RawEquipmentGroup
}

interface RawEquipmentGroup {
  readonly id: number;
  readonly name: string;
  readonly created_at: string;
  readonly updated_at: string;
  readonly deleted_at: string | null;
}

export interface RawEquipmentDetail extends RawEquipment {
  readonly equipment_group: RawEquipmentGroup;
  readonly agency: RawAgency;
}

interface RawCheckoutEquipment {
  readonly id: number;
  readonly warehouse_checkout_id: number;
  readonly disposed: number;
  readonly equipment_id: number;
  readonly notes: string | null;
  readonly created_at: string;
  readonly updated_at: string;
  readonly deleted_at: string | null;
  readonly equipment: RawEquipment;
  readonly lost: number;
  readonly returned_damaged: number;
}

interface RawMaterialGroup {
  readonly id: number;
  readonly name: string;
  readonly created_at: string;
  readonly updated_at: string;
  readonly deleted_at: string | null;
}

interface RawMeasurementUnit {
  readonly id: number;
  readonly name: string;
  readonly symbol: string;
  readonly created_at: string;
  readonly updated_at: string;
  readonly deleted_at: string | null;
}

interface RawMaterial {
  readonly id: number;
  readonly name: string;
  readonly code: string;
  readonly material_group_id: number;
  readonly measurement_unit_id: number;
  readonly reserved_inventory: number;
  readonly total_inventory: number;
  readonly created_at: string;
  readonly updated_at: string;
  readonly deleted_at: string | null;
  readonly material_group: RawMaterialGroup
}

export interface RawMaterialDetail extends RawMaterial {
  readonly material_group: RawMaterialGroup;
  readonly measurement_unit: RawMeasurementUnit;
}

interface RawCheckoutMaterialGroup {
  readonly id: number;
  readonly name: string;
}

interface RawCheckoutMaterialItem {
  readonly id: number;
  readonly name: string;
  readonly code: string;
  readonly material_group: RawCheckoutMaterialGroup | null;
}

export interface RawCheckoutMaterial {
  readonly id: number;
  readonly quantity_at_checkout: number;
  readonly quantity_at_checkin: number | null;
  readonly notes: string | null;
  readonly material: RawCheckoutMaterialItem;
}

export interface RawWarehouseCheckout {
  readonly id: number;
  readonly warehouse_id: number;
  readonly checked_out_by_user_id: number;
  readonly checked_in_by_user_id: number | null;
  readonly received_by_user_id: number;
  readonly checked_out_station_id: number;
  readonly checked_in_station_id: number | null;
  readonly status: number;
  readonly notes: string | null;
  readonly checked_out_at: string;
  readonly checked_in_at: string | null;
  readonly created_at: string;
  readonly updated_at: string;
  readonly deleted_at: string | null;
  readonly checked_in_by_user: RawCheckoutUser | null;
  readonly warehouse: RawWarehouse;
  readonly checked_out_by_user: RawCheckoutUser;
  readonly received_by_user: RawCheckoutUser;
  readonly checkout_equipments: readonly RawCheckoutEquipment[];
  readonly checkout_materials: readonly RawCheckoutMaterial[];
  readonly status_label: string;
}

// ─── Parse functions ─────────────────────────────────────────────────────────

function parseAgency(raw: RawAgency): IAgency {
  return {
    id: raw.id,
    name: raw.name,
    acronym: raw.acronym,
    areaPlanId: raw.area_plan_id,
    createdAt: raw.created_at,
    updatedAt: raw.updated_at,
    deletedAt: raw.deleted_at,
    canManageAnyAgency: raw.can_manage_any_agency,
  };
}

function parseWarehouse(raw: RawWarehouse): IWarehouse {
  return {
    id: raw.id,
    name: raw.name,
    agency: parseAgency(raw.agency),
    hasAccess: raw.hasAccess,
  };
}

function parseCheckoutUser(raw: RawCheckoutUser): ICheckoutUser {
  return {
    id: raw.id,
    representationName: raw.representation_name,
    registration: raw.registration,
    login: raw.login,
  };
}

function parseEquipment(raw: RawEquipment): IEquipment {
  return {
    id: raw.id,
    name: raw.name,
    code: raw.code,
    serialNumber: raw.serial_number,
    warehouseId: raw.warehouse_id,
    equipmentId: raw.equipment_id,
    agencyId: raw.agency_id,
    isAvailable: raw.is_available,
    active: raw.active,
    createdAt: raw.created_at,
    updatedAt: raw.updated_at,
    deletedAt: raw.deleted_at,
    hasAccess: raw.hasAccess,
    equipmentGroup: parseEquipmentGroup(raw.equipment_group)
  };
}

function parseCheckoutEquipment(raw: RawCheckoutEquipment): ICheckoutEquipment {
  return {
    id: raw.id,
    warehouseCheckoutId: raw.warehouse_checkout_id,
    equipmentId: raw.equipment_id,
    notes: raw.notes,
    createdAt: raw.created_at,
    updatedAt: raw.updated_at,
    deletedAt: raw.deleted_at,
    equipment: parseEquipment(raw.equipment),
    disposed: raw.disposed,
    lost: raw.lost,
    returnedDamaged: raw.returned_damaged
  };
}

function parseMaterialGroup(raw: RawMaterialGroup): IMaterialGroup {
  return {
    id: raw.id,
    name: raw.name,
    createdAt: raw.created_at,
    updatedAt: raw.updated_at,
    deletedAt: raw.deleted_at,
  };
}

function parseMeasurementUnit(raw: RawMeasurementUnit): IMeasurementUnit {
  return {
    id: raw.id,
    name: raw.name,
    symbol: raw.symbol,
    createdAt: raw.created_at,
    updatedAt: raw.updated_at,
    deletedAt: raw.deleted_at,
  };
}

function parseMaterial(raw: RawMaterial): IMaterial {
  return {
    id: raw.id,
    name: raw.name,
    code: raw.code,
    materialGroupId: raw.material_group_id,
    measurementUnitId: raw.measurement_unit_id,
    createdAt: raw.created_at,
    totalInventory: raw.total_inventory,
    reservedInventory: raw.reserved_inventory,
    updatedAt: raw.updated_at,
    deletedAt: raw.deleted_at,
    materialGroup: parseMaterialGroup(raw.material_group)
  };
}

export function parseMaterialDetail(raw: RawMaterialDetail): IMaterialDetail {
  return {
    ...parseMaterial(raw),
    materialGroup: parseMaterialGroup(raw.material_group),
    measurementUnit: parseMeasurementUnit(raw.measurement_unit),
  };
}


function parseCheckoutMaterialItem(raw: RawCheckoutMaterialItem): ICheckoutMaterialItem {
  return {
    id: raw.id,
    name: raw.name,
    code: raw.code,
    materialGroup: raw.material_group
      ? { id: raw.material_group.id, name: raw.material_group.name }
      : null,
  };
}

function parseCheckoutMaterial(raw: RawCheckoutMaterial): ICheckoutMaterial {
  return {
    id: raw.id,
    quantityAtCheckout: raw.quantity_at_checkout,
    quantityAtCheckin: raw.quantity_at_checkin,
    notes: raw.notes,
    material: parseCheckoutMaterialItem(raw.material),
  };
}

function parseEquipmentGroup(raw: RawEquipmentGroup): IEquipmentGroup {
  return {
    id: raw.id,
    name: raw.name,
    createdAt: raw.created_at,
    updatedAt: raw.updated_at,
    deletedAt: raw.deleted_at,
  };
}

export function parseEquipmentDetail(raw: RawEquipmentDetail): IEquipmentDetail {
  return {
    ...parseEquipment(raw),
    equipmentGroup: parseEquipmentGroup(raw.equipment_group),
    agency: parseAgency(raw.agency),
  };
}

export function parseWarehouseCheckout(raw: RawWarehouseCheckout): IWarehouseCheckout {
  return {
    id: raw.id,
    warehouseId: raw.warehouse_id,
    checkedOutByUserId: raw.checked_out_by_user_id,
    checkedInByUserId: raw.checked_in_by_user_id,
    receivedByUserId: raw.received_by_user_id,
    checkedOutStationId: raw.checked_out_station_id,
    checkedInStationId: raw.checked_in_station_id,
    status: raw.status,
    notes: raw.notes,
    checkedOutAt: raw.checked_out_at,
    checkedInAt: raw.checked_in_at,
    createdAt: raw.created_at,
    updatedAt: raw.updated_at,
    deletedAt: raw.deleted_at,
    checkedInByUser: raw.checked_in_by_user ? parseCheckoutUser(raw.checked_in_by_user) : null,
    warehouse: parseWarehouse(raw.warehouse),
    checkedOutByUser: parseCheckoutUser(raw.checked_out_by_user),
    receivedByUser: parseCheckoutUser(raw.received_by_user),
    checkoutEquipments: raw.checkout_equipments.map(parseCheckoutEquipment),
    checkoutMaterials: raw.checkout_materials.map(parseCheckoutMaterial),
    statusLabel: raw.status_label,
  };
}
